/**
 * The tractor beam's story (04 §5): it opens, catches, captures; the rescue and the docking.
 * The warble that holds while it is out is ambience (ambient.ts). Pure.
 */

import { midiToHz, semitones } from '../notes';
import { adsr, type VoiceSpec } from '../voice';
import { BEAM_CENTS, BEAM_HZ, BEAM_RATE } from './ambient';
import { BASS, LEAD, SECOND, type Sfx, tone, tune } from './kit';

/** Tones in the opening and closing sweep; at the beam's own rate that is a little over half a second. */
const SWEEP_STEPS = 14;

/** The beam's two tones in turn while both slide from `from` to `to`, Hz. */
function sweep(from: number, to: number): VoiceSpec {
  const stepTime = 1 / (BEAM_RATE * 2);
  const steps = Array.from({ length: SWEEP_STEPS }, (_, i) => {
    const hz = from * Math.pow(to / from, i / (SWEEP_STEPS - 1));
    return i % 2 === 0 ? hz : semitones(hz, BEAM_CENTS / 100);
  });
  return {
    ...tone('pulse', from, SWEEP_STEPS * stepTime, adsr(0.01, 0.1, 0.8, 0.08), 0.15),
    width: 0.5,
    steps,
    stepTime,
    once: true,
  };
}

/** The beam opens: the warble rising an octave to where the held one sits. */
export const beamOn: Sfx = () => [sweep(BEAM_HZ / 2, BEAM_HZ)];

/** The beam is gone: the same warble falling away. */
export const beamOff: Sfx = () => [sweep(BEAM_HZ, BEAM_HZ / 2)];

/** The beam has the fighter: a whirl of two tones a fourth apart, climbing as the ship spins up (1.3 s). */
export const caught: Sfx = () => {
  const steps = Array.from({ length: 44 }, (_, i) => midiToHz(62 + i * 0.4 + (i % 2) * 5));
  return [{
    ...tone('pulse', steps[0] ?? 294, 1.3, adsr(0.02, 0.2, 0.8, 0.12), 0.19),
    width: 0.25,
    steps,
    stepTime: 0.03,
    once: true,
  }];
};

/**
 * FIGHTER CAPTURED: a minor, sinking phrase - three falling arpeggios, A minor, E, A minor, each
 * starting a step lower, down to a held A with its minor third (1.8 s).
 */
export const captured: Sfx = () => [
  ...tune('C6 A5 E5 . | B5 G#5 E5 . | A5 E5 C5 . | A4 . . . . .', 0.1, LEAD),
  ...tune('-  .  .  . | -  .   .  . | -  .  .  . | C5 . . . . .', 0.1, SECOND),
  ...tune('A3 .  .  . | E3 .   .  . | A2 .  .  . | A2 . . . . .', 0.1, BASS),
];

/** Rescued, the captive spinning free: a G major arpeggio rising two octaves, echoed a step behind. */
export const rescued: Sfx = () => [
  ...tune('D5 G5 B5 D6 G6 B6 . D7 . . .', 0.075, LEAD),
  ...tune('- D5 G5 B5 D6 G6 . B6 . . .', 0.075, SECOND),
  ...tune('G3 . . . G4 . . G3 . . .', 0.075, BASS),
];

/** Docked, two ships now one: a bright two-note lock, up a fourth to the tonic. */
export const docked: Sfx = () => [
  ...tune('D6 G6 . .', 0.06, LEAD),
  ...tune('G5 B5 . .', 0.06, SECOND),
];
